/* LEGEND — floating STOCK INFO / NEWS card. Ported from legend/news.jsx.
   Company snapshot (about + session stats) over the latest stored headlines.
   Data: `about` = /api/symbols/:sym company fields (description, sector,
   market cap — FMP profile via Mongo), `news` = symbols.news (written by the
   news-service, hydrate + news:{SYM} WS patch), float from the supply model,
   NBBO subscribed HERE (quotes are the one high-frequency feed this card
   touches — the RrCard rule: frequent feeds re-render the widget, never the
   Terminal tree). Draggable / closable — mirrors the position-card chrome. */
import React from 'react';
import { Icon } from './Widgets';
import { fmtPx4, fmtSignedPx, fmtPct, fmtSharesShort, fmtMoneyShort } from '../lib/format';
import { subscribeQuotes } from '../data/source';

const { useState: useStateS, useEffect: useEffectS, useRef: useRefS } = React;
const h = React.createElement;

/** Wire tag from the Alpaca `source` field. The prototype's PR|SEC|WIRE set,
 *  derived instead of invented: press-release wires → PR, everything else
 *  (Benzinga is the whole Alpaca feed today) → WIRE. Shared with NewsRail. */
export function newsTag(source: string): string {
  const s = (source || '').toLowerCase();
  if (s.includes('globenewswire') || s.includes('prnewswire') || s.includes('businesswire')
    || s.includes('accesswire') || s.includes('newsfile')) return 'PR';
  if (s.includes('sec')) return 'SEC';
  return 'WIRE';
}

export function ageOf(iso: string | Date | null | undefined, nowMs: number): string {
  if (!iso) return '—';
  const t = new Date(iso as any).getTime();
  if (!Number.isFinite(t)) return '—';
  const m = Math.max(0, Math.floor((nowMs - t) / 60000));
  if (m < 1) return 'now';
  if (m < 60) return m + 'm';
  const hr = Math.floor(m / 60);
  if (hr < 24) return hr + 'h';
  return Math.floor(hr / 24) + 'd';
}

export function StockInfoCard(props: any) {
  const { symbol, name, lastPrice, prevClose, meta, about, news, floatShares, pos, onMove, onClose } = props;
  const cardRef = useRefS<any>(null);

  // Live NBBO — owned by the card so quote frames never re-render Terminal.
  const [quote, setQuote] = useStateS<any>(null);
  useEffectS(() => {
    setQuote(null);
    const off = subscribeQuotes(symbol, (q: any) => setQuote(q));
    return () => off();
  }, [symbol]);

  // Age ticker: quotes redraw the card on a live tape, but a quiet symbol must
  // not freeze "2m ago" the way the prototype's hardcoded strings did.
  const [nowMs, setNowMs] = useStateS(Date.now());
  useEffectS(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const ox = (pos.x == null && cardRef.current) ? cardRef.current.offsetLeft : (pos.x || 0);
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  const chg = prevClose ? (lastPrice - prevClose) / prevClose : 0;
  const up = chg >= 0;
  const prec = lastPrice > 0 && lastPrice < 1 ? 4 : 2;
  const bid = quote?.bid, ask = quote?.ask;
  const spread = bid != null && ask != null ? Math.max(0, ask - bid) : null;
  const floatSh = floatShares;
  const rvol = meta && meta.rvol > 0 ? meta.rvol : null;
  const sectorLine = about
    ? [about.sector, about.industry, about.exchange].filter(Boolean).join(' · ')
    : '';

  const stat = (k: string, v: any, tone?: string | null) => h('div', { className: 'si-stat', key: k },
    h('span', { className: 'si-sk' }, k),
    h('span', { className: 'si-sv num' + (tone ? ' ' + tone : '') }, v));

  const items: any[] = (news && news.items) || [];
  const latest = items[0] || null;
  const older = items.slice(1, 4);

  const story = (n: any) => h('div', { className: 'si-story' },
    h('div', { className: 'si-story-top' },
      h('span', { className: 'si-story-tag' }, newsTag(n.source)),
      h('span', { className: 'si-story-age mono' }, ageOf(n.publishedAt, nowMs) + ' ago')),
    n.url
      ? h('a', { className: 'si-story-title', href: n.url, target: '_blank', rel: 'noreferrer' }, n.headline)
      : h('div', { className: 'si-story-title' }, n.headline));

  const cardStyle = pos.x == null ? { right: '14px', top: pos.y + 'px' } : { left: pos.x + 'px', top: pos.y + 'px' };
  return h('div', { className: 'pos-card si-card', style: cardStyle, ref: cardRef },
    h('div', { className: 'pc-head', onPointerDown: startDrag },
      h('span', { className: 'pc-sym' }, symbol),
      h('span', { className: 'si-tag' }, 'Overview'),
      h('div', { className: 'pc-actions' },
        h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x()))),

    h('div', { className: 'pc-body si-body' },
      h('div', { className: 'si-name' }, (about && about.name) || name || symbol),
      sectorLine ? h('div', { className: 'si-sector' }, sectorLine) : null,

      h('div', { className: 'si-hero' },
        h('span', { className: 'si-px num' }, '$' + fmtPx4(lastPrice)),
        h('span', { className: 'si-chg num ' + (up ? 'up' : 'down') },
          fmtSignedPx(lastPrice - prevClose), ' (', fmtPct(chg), ')')),

      bid != null && ask != null ? h('div', { className: 'si-nbbo' },
        h('span', { className: 'sn-lab' }, 'BID'),
        h('span', { className: 'sn-v num up' }, bid.toFixed(prec)),
        h('span', { className: 'sn-x' }, '×'),
        h('span', { className: 'sn-v num down' }, ask.toFixed(prec)),
        h('span', { className: 'sn-lab' }, 'ASK'),
        h('span', { className: 'sn-spr num' }, 'spread $' + (spread as number).toFixed(prec))) : null,

      h('div', { className: 'si-grid' },
        stat('Prev close', '$' + fmtPx4(prevClose)),
        stat('Market cap', about && about.marketCap ? fmtMoneyShort(about.marketCap) : '—'),
        stat('Avg volume', meta && meta.vol != null ? meta.vol.toFixed(1) + 'M' : '—'),
        stat('Rel. volume', rvol ? rvol.toFixed(1) + '×' : '—', rvol && rvol >= 5 ? 'up' : null),
        stat('Public float', floatSh ? fmtSharesShort(floatSh) : '—'),
        stat('Spread', spread != null ? '$' + spread.toFixed(prec) : '—')),

      about && about.description ? h('div', { className: 'si-about' }, about.description) : null,

      latest ? h('div', { className: 'si-news' },
        h('div', { className: 'si-news-h' }, 'Latest'),
        story(latest),
        older.length ? h('div', { className: 'si-prev' },
          older.map((n: any, i: number) => h('div', { className: 'si-prev-row', key: n.id ?? i },
            h('span', { className: 'si-story-age mono' }, ageOf(n.publishedAt, nowMs)),
            n.url
              ? h('a', { className: 'si-prev-title', href: n.url, target: '_blank', rel: 'noreferrer' }, n.headline)
              : h('span', { className: 'si-prev-title' }, n.headline)))) : null) : null));
}
